/**
 * Multiple Product Options JavaScript (full updated)
 * - Tracks selected variants (with priceRaw and optional signed priceDelta)
 * - Renders displayed total = main product price + sum(modifiers)
 * - Works with single- and multi-select sections, preselected DOM states
 * - Integrates with existing add-to-cart flow
 */

class MultipleProductOptions {
  constructor() {
    this.selectedVariants = new Map(); // key: sectionTitle, value: {id, price, priceRaw, priceDelta, element} OR array of same
    this.mandatorySections = new Set();
    this.init();
  }

  init() {
    this.setupAccordions();
    this.setupVariantSelection();
    this.preselectDefaultVariant();
    this.setupAddToCart();
    this.setupQuantityInput();
    this.initializeMandatorySections();
  }

  /**
   * Setup accordion functionality
   */
  setupAccordions() {
    const accordions = document.querySelectorAll('.option-accordion');

    accordions.forEach((accordion, index) => {
      const title = accordion.querySelector('.option-title');
      const content = accordion.querySelector('.option-content');

      // Add chevron icon if not present
      if (title && !title.querySelector('.chevron')) {
        const chevron = document.createElement('span');
        chevron.className = 'chevron';
        chevron.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M26.7081 12.7076L16.7081 22.7076C16.6152 22.8005 16.5049 22.8743 16.3835 22.9246C16.2621 22.975 16.132 23.0009 16.0006 23.0009C15.8691 23.0009 15.739 22.975 15.6176 22.9246C15.4962 22.8743 15.3859 22.8005 15.2931 22.7076L5.29306 12.7076C5.10542 12.5199 5 12.2654 5 12.0001C5 11.7347 5.10542 11.4802 5.29306 11.2926C5.4807 11.1049 5.73519 10.9995 6.00056 10.9995C6.26592 10.9995 6.52042 11.1049 6.70806 11.2926L16.0006 20.5863L25.2931 11.2926C25.386 11.1997 25.4963 11.126 25.6177 11.0757C25.7391 11.0254 25.8692 10.9995 26.0006 10.9995C26.132 10.9995 26.2621 11.0254 26.3835 11.0757C26.5048 11.126 26.6151 11.1997 26.7081 11.2926C26.801 11.3855 26.8747 11.4958 26.9249 11.6172C26.9752 11.7386 27.0011 11.8687 27.0011 12.0001C27.0011 12.1315 26.9752 12.2616 26.9249 12.383C26.8747 12.5044 26.801 12.6147 26.7081 12.7076Z" fill="black"/>
          </svg>
        `;
        title.appendChild(chevron);
      }

      // Add click handler
      if (title) {
        title.addEventListener('click', () => {
          this.toggleAccordion(accordion);
        });
      }

      // Open only the first accordion by default
      if (index === 0) {
        this.openAccordion(accordion);
      } else {
        this.closeAccordion(accordion);
      }
    });
  }

  toggleAccordion(accordion) {
    const isActive = accordion.classList.contains('active');
    if (isActive) {
      this.closeAccordion(accordion);
    } else {
      this.openAccordion(accordion);
    }
  }

  openAccordion(accordion) {
    accordion.classList.add('active');
    const content = accordion.querySelector('.option-content');
    if (content) content.style.display = 'block';
  }

  closeAccordion(accordion) {
    accordion.classList.remove('active');
    const content = accordion.querySelector('.option-content');
    if (content) content.style.display = 'none';
  }

  /**
   * Setup variant selection functionality
   */
  setupVariantSelection() {
    const variantItems = document.querySelectorAll('.product-variant-item');

    variantItems.forEach(item => {
      item.addEventListener('click', () => {
        this.selectVariant(item);
      });

      // Add keyboard support
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectVariant(item);
        }
      });

      // Make focusable
      if (!item.hasAttribute('tabindex')) item.setAttribute('tabindex', '0');
    });
  }

  /**
   * Preselect any default .selected items present in DOM (supports multiple preselected per section)
   */
  preselectDefaultVariant() {
    const preselected = document.querySelectorAll('.product-variant-item.selected');
    if (preselected && preselected.length) {
      preselected.forEach(item => {
        const productListContent = item.closest('.product-list-content');
        const isMulti = productListContent && productListContent.dataset && productListContent.dataset.multiple === 'true';
        const sectionTitle = this.getSectionTitle(item);
        const variantId = item.dataset.id;

        const priceRaw = parseInt(item.dataset.priceRaw || item.dataset.priceraw || 0, 10) || 0;
        // priceDelta may be undefined or empty string; parseInt will produce NaN => handle below
        const priceDeltaRaw = (typeof item.dataset.priceDelta !== 'undefined' && item.dataset.priceDelta !== '') ?
                              parseInt(item.dataset.priceDelta, 10) : null;

        if (isMulti) {
          if (!this.selectedVariants.has(sectionTitle)) {
            this.selectedVariants.set(sectionTitle, []);
          }
          const selectedList = this.selectedVariants.get(sectionTitle);
          if (!selectedList.find(v => v.id === variantId)) {
            selectedList.push({
              id: variantId,
              price: item.dataset.price || '',
              priceRaw: priceRaw,
              priceDelta: priceDeltaRaw,
              element: item
            });
          }
        } else {
          this.selectedVariants.set(sectionTitle, {
            id: variantId,
            price: item.dataset.price || '',
            priceRaw: priceRaw,
            priceDelta: priceDeltaRaw,
            element: item
          });
        }

        // ensure hidden input matches DOM preselected state
        const hiddenInput = productListContent ? productListContent.querySelector('input[name="variant-id"]') : null;
        if (hiddenInput) {
          if (isMulti) {
            const ids = this.selectedVariants.get(sectionTitle).map(v => v.id);
            hiddenInput.value = ids.join(',');
          } else {
            hiddenInput.value = variantId;
          }
        }
      });

      // run validation and price render after preselects
      this.updateValidation();
      this.updateDisplayedPrice();
    }
  }

  /**
   * Select a variant item (supports multi-select for sections with data-multiple="true")
   */
  selectVariant(item) {
    const productListContent = item.closest('.product-list-content');
    const variantId = item.dataset.id;
    const sectionTitle = this.getSectionTitle(item);
    const isMulti = productListContent && productListContent.dataset && productListContent.dataset.multiple === 'true';

    const priceRaw = parseInt(item.dataset.priceRaw || item.dataset.priceraw || 0, 10) || 0;
    const priceDeltaRaw = (typeof item.dataset.priceDelta !== 'undefined' && item.dataset.priceDelta !== '') ?
                          parseInt(item.dataset.priceDelta, 10) : null;

    if (isMulti) {
      // Multi-select: toggle item on/off
      const isNowSelected = item.classList.toggle('selected');

      if (!this.selectedVariants.has(sectionTitle)) {
        this.selectedVariants.set(sectionTitle, []);
      }
      const selectedList = this.selectedVariants.get(sectionTitle);

      if (isNowSelected) {
        if (!selectedList.find(v => v.id === variantId)) {
          selectedList.push({
            id: variantId,
            price: item.dataset.price || '',
            priceRaw: priceRaw,
            priceDelta: priceDeltaRaw,
            element: item
          });
        }
      } else {
        const filtered = selectedList.filter(v => v.id !== variantId);
        if (filtered.length > 0) {
          this.selectedVariants.set(sectionTitle, filtered);
        } else {
          this.selectedVariants.delete(sectionTitle);
        }
      }

      // Update hidden input (comma-separated)
      const hiddenInput = productListContent ? productListContent.querySelector('input[name="variant-id"]') : null;
      if (hiddenInput) {
        const ids = this.selectedVariants.has(sectionTitle) ? this.selectedVariants.get(sectionTitle).map(v => v.id) : [];
        hiddenInput.value = ids.join(',');
      }
    } else {
      // Single-select: allow deselect by clicking the same item again
      const previousSelected = productListContent.querySelector('.product-variant-item.selected');

      if (previousSelected === item) {
        // Deselect the item
        item.classList.remove('selected');

        // Clear hidden input
        const hiddenInput = productListContent ? productListContent.querySelector('input[name="variant-id"]') : null;
        if (hiddenInput) hiddenInput.value = '';

        // Remove from selectedVariants map
        if (this.selectedVariants.has(sectionTitle)) {
          this.selectedVariants.delete(sectionTitle);
        }
      } else {
        // Normal single-select behaviour: switch selection
        if (previousSelected) previousSelected.classList.remove('selected');
        item.classList.add('selected');

        const hiddenInput = productListContent ? productListContent.querySelector('input[name="variant-id"]') : null;
        if (hiddenInput) hiddenInput.value = variantId;

        this.selectedVariants.set(sectionTitle, {
          id: variantId,
          price: item.dataset.price || '',
          priceRaw: priceRaw,
          priceDelta: priceDeltaRaw,
          element: item
        });
      }
    }

    // Update validation and displayed price
    this.updateValidation();
    this.updateDisplayedPrice();
  }

  /**
   * Get section title for a variant item
   */
  getSectionTitle(item) {
    const productListContent = item.closest('.product-list-content');
    const titleElement = productListContent ? productListContent.querySelector('.product-list-title') : null;
    return titleElement ? titleElement.textContent.trim() : '';
  }

  /**
   * Initialize mandatory sections tracking
   */
  initializeMandatorySections() {
    const mandatoryAccordions = document.querySelectorAll('.option-accordion[data-mandatory="true"]');

    mandatoryAccordions.forEach(accordion => {
      const productListContents = accordion.querySelectorAll('.product-list-content');

      productListContents.forEach(content => {
        const titleElement = content.querySelector('.product-list-title');
        if (titleElement && titleElement.querySelector('span')) {
          const title = titleElement.textContent.trim();
          this.mandatorySections.add(title);
        }
      });
    });
  }

  /**
   * Update validation state
   */
  updateValidation() {
    const allMandatorySelected = Array.from(this.mandatorySections).every(section =>
      this.selectedVariants.has(section)
    );

    const addToCartBtn = document.querySelector('.custom-option-button');
    if (addToCartBtn) {
      if (allMandatorySelected) {
        addToCartBtn.classList.remove('mandatory-error-state');
      } else {
        addToCartBtn.classList.add('mandatory-error-state');
      }
    }

    this.hideError();
  }

  showError(message = 'ALL MANDATORY ITEMS MUST BE SELECTED') {
    let errorElement = document.querySelector('.mandatory-error');

    if (!errorElement) {
      errorElement = this.createErrorElement(message);
      const container = document.querySelector('.custom-options-container');
      container.insertBefore(errorElement, container.firstChild);
    }

    errorElement.classList.add('show');

    // Scroll to error
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hideError() {
    const errorElement = document.querySelector('.mandatory-error');
    if (errorElement) {
      errorElement.classList.remove('show');
    }
  }

  createErrorElement(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'mandatory-error';

    const icon = document.createElement('div');
    icon.className = 'mandatory-error-icon';
    icon.textContent = '×';

    const text = document.createElement('span');
    text.textContent = message;

    errorDiv.appendChild(icon);
    errorDiv.appendChild(text);

    return errorDiv;
  }

  setupAddToCart() {
    const addToCartBtn = document.querySelector('.custom-option-button');

    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }
  }

  async handleAddToCart() {
    // Validate mandatory selections
    if (!this.validateMandatorySelections()) {
      return;
    }

    const addToCartBtn = document.querySelector('.custom-option-button');
    const originalText = addToCartBtn ? addToCartBtn.textContent : '';
    const mainId =addToCartBtn.dataset.id;

    // Show loading state
    if (addToCartBtn) {
      addToCartBtn.classList.add('loading');
      addToCartBtn.disabled = true;
    }

    try {
      // Get quantity
      const quantity = this.getQuantity();

      // Prepare cart items
      const cartItems = this.prepareCartItems(quantity);

      // Add items to cart
      await this.addItemsToCart(cartItems, mainId);

      // Open cart drawer
      this.openCartDrawer();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Error adding items to cart. Please try again.');
    } finally {
      // Reset button state
      if (addToCartBtn) {
        addToCartBtn.classList.remove('loading');
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = originalText;
      }
    }
  }

  validateMandatorySelections() {
    const missingSelections = [];

    this.mandatorySections.forEach(section => {
      if (!this.selectedVariants.has(section)) {
        missingSelections.push(section);
      }
    });

    if (missingSelections.length > 0) {
      this.showError();

      // Scroll to first missing section
      const firstMissing = missingSelections[0];
      const accordion = this.findAccordionBySection(firstMissing);
      if (accordion) {
        this.openAccordion(accordion);
        accordion.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return false;
    }

    return true;
  }

  findAccordionBySection(sectionTitle) {
    const accordions = document.querySelectorAll('.option-accordion');

    for (const accordion of accordions) {
      const productListContents = accordion.querySelectorAll('.product-list-content');

      for (const content of productListContents) {
        const titleElement = content.querySelector('.product-list-title');
        if (titleElement && titleElement.textContent.trim() === sectionTitle) {
          return accordion;
        }
      }
    }

    return null;
  }

  getQuantity() {
    const quantityInput = document.querySelector('input.quantity__input');
    return quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
  }

  prepareCartItems(quantity) {
    const items = [];

    // Add main product
    const mainProductInput = document.querySelector('input.main-product-input');
    if (mainProductInput) {
      items.push({
        id: mainProductInput.value,
        quantity: quantity
      });
    }

    // Add selected variants
    this.selectedVariants.forEach((variant, sectionTitle) => {
      if (Array.isArray(variant)) {
        variant.forEach(v => {
          items.push({
            id: v.id,
            quantity: quantity
          });
        });
      } else if (variant && variant.id) {
        items.push({
          id: variant.id,
          quantity: quantity
        });
      }
    });

    return items;
  }

  async addItemsToCart(items, mainId) {
    // Generate timestamp in seconds to differentiate between different additions
    const timestamp = Math.floor(Date.now() / 1000);
    
    // Prepare all items for single request
    const formData = {
      'items': items.map(item => ({
        'id': item.id,
        'quantity': item.quantity,
        'properties': {
          '_main_id': mainId,
          '_main_id_timestamp': timestamp
        }
      }))
    };

    // Get cart drawer element for sections to render
    const cartDrawer = document.querySelector('cart-drawer');
    let sectionsToRender = [];
    try {
      sectionsToRender = cartDrawer ? (typeof cartDrawer.getSectionsToRender === 'function' ? cartDrawer.getSectionsToRender().map(section => section.id) : []) : [];
    } catch (e) {
      sectionsToRender = [];
    }

    // Add sections to form data for cart drawer update
    if (sectionsToRender.length > 0) {
      formData.sections = sectionsToRender;
      formData.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to add items to cart');
    }

    const result = await response.json();

    // Update cart drawer if it exists
    if (cartDrawer && result.sections) {
      if (typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(result);
      } else {
        // fallback: set innerHTML where possible
        try {
          cartDrawer.innerHTML = result.sections_html || cartDrawer.innerHTML;
        } catch (e) {
          // noop
        }
      }
    }

    // Ensure cart drawer is not in empty state after adding items
    if (cartDrawer) {
      cartDrawer.classList.remove('is-empty');
      const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.classList.remove('is-empty');
      }
    }

    return result;
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      cartDrawer.classList.remove('is-empty');
      const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.classList.remove('is-empty');
      }

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.classList.add('open');
      }
    } else {
      window.location.href = '/cart';
    }
  }

  setupQuantityInput() {
    const quantityInput = document.querySelector('input.quantity__input');
    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        this.updateValidation();
        this.updateDisplayedPrice();
      });
    }
  }

  /**
   * Format cents to currency string using Intl.NumberFormat
   * cents - integer (shop money integer, e.g. 1999 for 19.99)
   */
  formatMoney(cents, currency) {
    try {
      const value = (parseInt(cents, 10) || 0) / 100;
      const locale = navigator.language || 'en-US';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(value);
    } catch (e) {
      return ((parseInt(cents, 10) || 0) / 100).toFixed(2) + ' ' + (currency || '');
    }
  }

  /**
   * Compute and render the total price = main product + sum of selected variant modifiers.
   * - For each selected variant: prefer signed data-price-delta (can be negative).
   * - If data-price-delta not present, fall back to data-price-raw (positive).
   *
   * Requires a DOM element: .mpo-selected-price
   * - data-currency attribute (ISO currency)
   * - data-main-price attribute (cents of main product)
   */
  updateDisplayedPrice() {
    const priceEls = document.querySelectorAll('.price-item--update');
    if (!priceEls || !priceEls.length) return;

    priceEls.forEach(priceEl => {
      const currency = priceEl.dataset.currency || 'GBP';

      // Read main product price (in cents)
      const mainRaw = parseInt(priceEl.dataset.priceProduct || priceEl.dataset.mainPrice || priceEl.dataset.defaultPrice || 0, 10) || 0;

      // Sum selected modifiers
      let modifiersTotal = 0;

      this.selectedVariants.forEach(value => {
        if (Array.isArray(value)) {
          value.forEach(v => {
            const el = v.element;
            if (!el) return;
            const deltaAttr = (typeof el.dataset.priceDelta !== 'undefined' && el.dataset.priceDelta !== '') ? el.dataset.priceDelta : null;
            if (deltaAttr !== null) {
              const d = parseInt(deltaAttr, 10);
              modifiersTotal += isNaN(d) ? 0 : d;
            } else {
              const raw = parseInt(v.priceRaw || el.dataset.priceRaw || el.dataset.priceraw || 0, 10);
              modifiersTotal += isNaN(raw) ? 0 : raw;
            }
          });
        } else if (value && value.id) {
          const el = value.element;
          if (!el) return;
          const deltaAttr = (typeof el.dataset.priceDelta !== 'undefined' && el.dataset.priceDelta !== '') ? el.dataset.priceDelta : null;
          if (deltaAttr !== null) {
            const d = parseInt(deltaAttr, 10);
            modifiersTotal += isNaN(d) ? 0 : d;
          } else {
            const raw = parseInt(value.priceRaw || el.dataset.priceRaw || el.dataset.priceraw || 0, 10);
            modifiersTotal += isNaN(raw) ? 0 : raw;
          }
        }
      });

      const totalCents = mainRaw + modifiersTotal;
      priceEl.textContent = this.formatMoney(totalCents, currency);
    });
  }

  removeAllSelectedVariants() {
  const allItems = document.querySelectorAll('.product-variant-item.selected');
  allItems.forEach(i => i.classList.remove('selected'));

  this.selectedVariants.clear();
  this.updateDisplayedPrice();
  this.updateValidation();
}

}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.custom-options-container')) {
    window.MultipleProductOptions = new MultipleProductOptions();
  }
});

// Export for potential external use (keeps previous behavior)
window.MultipleProductOptionsClass = MultipleProductOptions;

// Listen for Shopify main variant changes
document.addEventListener('variant:change', () => {
  if (window.MultipleProductOptions) {
    window.MultipleProductOptions.removeAllSelectedVariants();
  }
});

// Fallback for themes without variant:change event
document.addEventListener('change', e => {
  if (e.target.name === 'id' && window.MultipleProductOptions) {
    window.MultipleProductOptions.removeAllSelectedVariants();
  }
});
